/**
 * Ritual Completed Notification Component
 * Displays a notification when a ritual is marked as completed by admin
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface RitualCompletedNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface RitualCompletedNotificationData {
  ritualId?: string
  ritualName?: string
  ritualCoverImageUrl?: string | null
  communityId?: string
  communityName?: string
  isPaid?: boolean
  fee?: number | null
  ritualType?: string
  duration?: string
}

export function RitualCompletedNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: RitualCompletedNotificationProps) {
  const data = notification.data as RitualCompletedNotificationData

  const ritualId = data.ritualId
  const ritualName = data.ritualName || "a ritual"
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (notification.triggeredByUser?.id) {
      window.location.href = `/profile/${notification.triggeredByUser.id}`
    }
  }

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (ritualId) {
      window.location.href = `/rituals/${ritualId}`
    }
  }

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (data.communityId) {
      window.location.href = `/communities/${data.communityId}`
    }
  }

  const renderMessage = () => {
    const action = notification.action
    const triggeredBy = notification.triggeredByUser
    const userName = triggeredBy?.name || triggeredBy?.username || "Someone"

    if (action === "ritual_completed") {
      return (
        <>
          <button
            onClick={handleUserClick}
            className="font-semibold hover:underline cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {userName}
          </button>
          {" marked "}
          <span className="font-semibold">{ritualName}</span>
          {" as completed"}
        </>
      )
    }

    if (action === "community_added_to_ritual") {
      const communityName = data.communityName || ""
      return (
        <>
          <button
            onClick={handleUserClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {userName}
          </button>
          {" added "}
          <button
            onClick={handleRitualClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {ritualName}
          </button>
          {" ritual to "}
          <button
            onClick={handleCommunityClick}
            className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
            type="button"
          >
            {communityName}
          </button>
          {" community."}
        </>
      )
    }

    return notification.message
  }

  const getRitualSubtitle = () => {
    const isPaid = data.isPaid
    const fee = data.fee
    const ritualType = data.ritualType
    const duration = data.duration

    return (
      <div className="flex items-center gap-2 flex-wrap mt-2">
        <span
          className={`text-[13px] ${isPaid ? "bg-brand-terracotta" : "bg-brand-olive"} text-white px-2 py-0.5 rounded`}
        >
          {isPaid && fee ? `$${fee}` : "Free"}
        </span>
        {ritualType && <span className="text-white/80 text-[13px]">{ritualType}</span>}
        {duration && <span className="text-white/60 text-[13px]">{duration}</span>}
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px] pr-20">{renderMessage()}</div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Ritual Card (non-clickable) */}
      <div className="relative mt-2 h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden border border-card">
        {ritualCoverImageUrl ? (
          <img src={ritualCoverImageUrl} alt={ritualName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {ritualName}
            </span>
            {getRitualSubtitle()}
          </div>
        </div>
      </div>
    </div>
  )
}
